// Pure status → presentation mapping and the read-tracking predicate, pulled
// out of FeedbackPanel/FeedbackDetail so both the visual rule
// (doc/SUPPORT_CENTER_PLAN.md §0.3) and the "is this unread" rule are each
// defined exactly once and are unit-testable without React or a network.
import type { FeedbackStatus } from './api';
import type { MessageKey } from '../../../shared/i18n/messages';

export type FeedbackStatusIcon = 'check' | 'clock' | 'none';

export type FeedbackStatusPresentation = {
  toneClass: FeedbackStatus;
  icon: FeedbackStatusIcon;
  labelKey: MessageKey;
};

/**
 * §0.3's three tiers: replied = signal tint, pending = neutral tint, closed =
 * no background at all. Never --ds-win — DESIGN_SYSTEM.md §1.2 reserves green
 * for wins/payouts, and reusing it here would dilute that signal. Every tier
 * also carries an icon, not just a colour, per DESIGN_SYSTEM.md §8 ("all
 * states carry a non-colour signal").
 */
export function feedbackStatusPresentation(status: FeedbackStatus): FeedbackStatusPresentation {
  if (status === 'replied') return { toneClass: 'replied', icon: 'check', labelKey: 'feedbackStatusReplied' };
  if (status === 'closed') return { toneClass: 'closed', icon: 'none', labelKey: 'feedbackStatusClosed' };
  return { toneClass: 'pending', icon: 'clock', labelKey: 'feedbackStatusPending' };
}

/**
 * A feedback thread is unread exactly when it has a reply the player has not
 * opened yet. This single function backs BOTH the red dot (list row + tab
 * badge) and the gate on calling markFeedbackRead — deliberately the same
 * predicate, so those two can never end up disagreeing about an item's
 * unread state (see task-15 brief: "clearing one and not the other is worse
 * than clearing neither").
 */
export function isFeedbackUnread(item: { status: FeedbackStatus; read_at: number | null }): boolean {
  return item.status === 'replied' && item.read_at == null;
}
